import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAnglesRight,
  faClone,
  faPen,
  faPlus,
  faReceipt,
  faSearch,
  faSpinner,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { EmployeeTypeahead, type EmployeeSuggestion } from '../EmployeeTypeahead'
import DaisyDatePicker from '../DaisyDatePicker'
import { MoneyInput } from '../MoneyInput'
import { AdvanceDescription, LedgerSortChip, LockedBalanceBadge } from '../AdvanceTableCells'
import { hasEstablishedBalance, withEmployeeRunningBalances, type RunningBalanceRow } from '../profileAdvanceBalances'
import { useAuth } from '../../contexts/AuthContext'
import { useConfirmPassword } from '../ConfirmPasswordModal'
import { useDocumentViewer } from '../../contexts/DocumentViewerContext'
import PhotoUploadPreview from '../tally/PhotoUploadPreview'
import { compressImageFiles } from '../tally/types'
import {
  eeapService,
  MAX_ADVANCE_IMAGES,
  type AdvanceCategory,
  type AdvanceTransaction,
  type AdvanceTransactionInput,
} from '../../services/eeapService'
import { hrToolsService } from '../../services/hrToolsService'
import { getPayPeriodConfig, type PayPeriodConfig } from '../../services/payPeriodService'
import { DEFAULT_EEAP_PREAPPROVED_CATEGORIES } from '../../utils/eeapPreapprovedCategories'
import {
  DEFAULT_ADVANCE_CATEGORY_DEFS,
  mergeLedgerCategoryOptions,
} from '../../utils/advanceCategories'
import {
  DEFAULT_OPERATION_CONFIG,
  stepToPayDate,
} from '../../utils/controlCenterOperationConfig'
import {
  asLedgerCategory,
  categoryLabel,
  employeeBalances,
  filterJournalRows,
  formatLedgerDate,
  formatUsd,
  isDeductionRow,
  isSingleEmployeeJournal,
  journalBalanceTitle,
  ledgerDraftHasValue,
  moneyValueClass,
  periodTotals,
  rowsForJournalRunning,
  rowDay,
  searchQueries,
  splitCategoryKeys,
  type AdvanceCategoryDef,
  type CategoryFilter,
  type DateSort,
  type ItemCategoryFilter,
  type LedgerSortField,
  type EmployeeBalanceRow,
  type LedgerView,
} from './advanceLedgerViews'

function suggestionLabel(emp: EmployeeSuggestion) {
  return String(emp.preferredName || emp.name || emp.employeeId).trim()
}

function receiptUrls(row?: { imageUrls?: string[] | null }) {
  return Array.isArray(row?.imageUrls) ? row.imageUrls.filter(Boolean) : []
}

function openReceipt(
  openDocument: (title?: string, type?: string, url?: string | null) => void,
  urls: string[] | undefined,
  index = 0,
) {
  const list = receiptUrls({ imageUrls: urls })
  const url = list[index] || list[0]
  if (!url) return
  const title = list.length > 1 ? `Receipt (${index + 1} of ${list.length})` : 'Receipt'
  openDocument(title, 'Image', url)
}

function MoneyTd({ value, positiveGreen }: { value: number; positiveGreen?: boolean }) {
  return (
    <td className={moneyValueClass(value, { positiveGreen })}>
      {formatUsd(value)}
    </td>
  )
}

function EmployeeCell({ name, employeeId }: { name: string; employeeId: string }) {
  return (
    <>
      <div>{name || employeeId}</div>
      {name && name !== employeeId ? (
        <div className="text-xs text-base-content/50">{employeeId}</div>
      ) : null}
    </>
  )
}

type Draft = {
  id?: string
  employeeId: string
  employeeLabel: string
  date: string
  category: AdvanceCategory
  itemCategory: string
  amount: string
  employer: string
  description: string
  balance: string
  imageUrls: string[]
}

function emptyDraft(): Draft {
  return {
    employeeId: '',
    employeeLabel: '',
    date: new Date().toISOString().slice(0, 10),
    category: 'eeap',
    itemCategory: '',
    amount: '',
    employer: '0',
    description: '',
    balance: '',
    imageUrls: [],
  }
}

function draftFromRow(row: AdvanceTransaction): Draft {
  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeLabel: row.employeeName || row.employeeId,
    date: rowDay(row.date) || new Date().toISOString().slice(0, 10),
    category: asLedgerCategory(row.category),
    itemCategory: String(row.itemCategory || ''),
    amount: String(row.amount),
    employer: String(row.employerContribution),
    description: row.description,
    balance: row.establishedBalance != null ? String(row.establishedBalance) : '',
    imageUrls: receiptUrls(row),
  }
}

function Field({
  label,
  compact,
  children,
}: {
  label: string
  compact?: boolean
  children: React.ReactNode
}) {
  return (
    <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-1.5'} w-full`}>
      <span className={compact ? 'text-xs font-medium' : 'text-sm font-medium'}>{label}</span>
      {children}
    </div>
  )
}

const VIEWS: { id: LedgerView; label: string }[] = [
  { id: 'journal', label: 'Journal' },
  { id: 'balances', label: 'Balances' },
]

type JournalDisplay = 'posted' | 'excludeDeductions' | 'deductionsOnly' | 'includeScheduled'

const JOURNAL_DISPLAY_OPTIONS: { value: JournalDisplay; label: string }[] = [
  { value: 'posted', label: 'All posted' },
  { value: 'excludeDeductions', label: 'Exclude deductions' },
  { value: 'deductionsOnly', label: 'Deductions only' },
  { value: 'includeScheduled', label: 'Include scheduled' },
]

function journalDisplayFlags(display: JournalDisplay) {
  return {
    includeScheduled: display === 'includeScheduled',
    excludeDeductions: display === 'excludeDeductions',
    deductionsOnly: display === 'deductionsOnly',
  }
}

const HrAdvancesTab: React.FC = () => {
  const { hasPermission } = useAuth()
  const canDelete = hasPermission('adminPage')
  const { confirmPassword, confirmPasswordModal } = useConfirmPassword()
  const { openDocument } = useDocumentViewer()
  const [rows, setRows] = useState<AdvanceTransaction[]>([])
  const [hireDates, setHireDates] = useState<Record<string, string | null>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [view, setView] = useState<LedgerView>('journal')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [itemCategoryFilter, setItemCategoryFilter] = useState<ItemCategoryFilter>('all')
  const [dateSort, setDateSort] = useState<DateSort>('asc')
  const [sortField, setSortField] = useState<LedgerSortField>('date')
  const [itemCategories, setItemCategories] = useState<string[]>([...DEFAULT_EEAP_PREAPPROVED_CATEGORIES])
  const [ledgerCategories, setLedgerCategories] = useState(DEFAULT_ADVANCE_CATEGORY_DEFS)
  const [journalDisplay, setJournalDisplay] = useState<JournalDisplay>('posted')
  const [filterEmployee, setFilterEmployee] = useState<{ employeeId: string; label: string } | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [photoIndex, setPhotoIndex] = useState(0)
  const [payPeriodConfig, setPayPeriodConfig] = useState<PayPeriodConfig | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [data, categories, nextLedgerCategories, nextPayPeriod] = await Promise.all([
        eeapService.listTransactions(),
        hrToolsService.getEeapCategories().catch(() => [...DEFAULT_EEAP_PREAPPROVED_CATEGORIES]),
        hrToolsService.getAdvanceCategories().catch(() => [...DEFAULT_ADVANCE_CATEGORY_DEFS]),
        getPayPeriodConfig().catch(() => null),
      ])
      setRows(data.transactions)
      setHireDates(data.hireDates)
      if (categories.length) setItemCategories(categories)
      if (nextLedgerCategories.length) setLedgerCategories(nextLedgerCategories)
      if (nextPayPeriod) setPayPeriodConfig(nextPayPeriod)
    } catch (e) {
      setRows([])
      setHireDates({})
      setError(e instanceof Error ? e.message : 'Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!message) return
    const id = window.setTimeout(() => setMessage(null), 3000)
    return () => window.clearTimeout(id)
  }, [message])

  const { includeScheduled, excludeDeductions, deductionsOnly } = journalDisplayFlags(journalDisplay)

  const ledgerFilters = useMemo(() => ({
    queries: searchQueries(search, filters),
    dateFrom,
    dateTo,
    category: categoryFilter,
    itemCategory: itemCategoryFilter,
    dateSort,
    sortField,
    includeScheduled,
    excludeDeductions,
    deductionsOnly,
    employeeId: filterEmployee?.employeeId || '',
    categoryCatalog: ledgerCategories,
    hireDates,
  }), [search, filters, dateFrom, dateTo, categoryFilter, itemCategoryFilter, dateSort, sortField, includeScheduled, excludeDeductions, deductionsOnly, filterEmployee, ledgerCategories, hireDates])

  const journalRows = useMemo(
    () => filterJournalRows(
      withEmployeeRunningBalances(rowsForJournalRunning(rows, ledgerFilters)),
      ledgerFilters,
    ),
    [rows, ledgerFilters],
  )
  const showJournalBalance = useMemo(() => isSingleEmployeeJournal(journalRows), [journalRows])
  const journalTotals = useMemo(() => periodTotals(journalRows), [journalRows])
  const balanceRows = useMemo(() => employeeBalances(rows, ledgerFilters), [rows, ledgerFilters])
  const categoryOptions = useMemo(() => {
    const fromRows = rows.map((row) => String(row.category || '').trim()).filter(Boolean)
    return mergeLedgerCategoryOptions(ledgerCategories, fromRows)
  }, [ledgerCategories, rows])
  const defaultCategory = categoryOptions.find((item) => item.key === 'eeap')?.key
    || categoryOptions[0]?.key
    || 'eeap'
  const balanceCategoryKeys = useMemo(
    () => splitCategoryKeys(ledgerCategories, balanceRows),
    [ledgerCategories, balanceRows],
  )
  const balanceTotals = useMemo(() => {
    const byCategory: Record<string, number> = {}
    for (const key of balanceCategoryKeys) byCategory[key] = 0
    let total = 0
    let remainingAllowance = 0
    for (const row of balanceRows) {
      total += row.total
      remainingAllowance += Number(row.remainingAllowance || 0)
      for (const key of balanceCategoryKeys) {
        byCategory[key] = (byCategory[key] || 0) + (row.byCategory[key] || 0)
      }
    }
    return { byCategory, total, remainingAllowance }
  }, [balanceRows, balanceCategoryKeys])
  const showCategorySplit = categoryFilter === 'all'
  const itemCategoryOptions = useMemo(() => {
    const fromRows = rows.map((row) => String(row.itemCategory || '').trim()).filter(Boolean)
    return [...new Set([...itemCategories, ...fromRows])]
  }, [itemCategories, rows])
  const draftItemOptions = useMemo(() => {
    const current = draft.itemCategory.trim()
    if (current && !itemCategoryOptions.includes(current)) return [current, ...itemCategoryOptions]
    return itemCategoryOptions
  }, [draft.itemCategory, itemCategoryOptions])
  const draftCategoryOptions = useMemo(
    () => mergeLedgerCategoryOptions(categoryOptions, [draft.category]),
    [categoryOptions, draft.category],
  )

  const pinTerm = (term: string) => {
    const next = term.trim()
    if (!next) return
    setFilters((prev) => (prev.includes(next) ? prev : [...prev, next]))
    setSearch('')
  }

  const pinSearch = () => pinTerm(search)

  const hasActiveFilters = Boolean(
    search.trim()
    || filters.length
    || dateFrom
    || dateTo
    || categoryFilter !== 'all'
    || itemCategoryFilter !== 'all'
    || journalDisplay !== 'posted'
    || filterEmployee
  )

  const clearAllFilters = () => {
    setSearch('')
    setFilters([])
    setDateFrom('')
    setDateTo('')
    setCategoryFilter('all')
    setItemCategoryFilter('all')
    setJournalDisplay('posted')
    setFilterEmployee(null)
  }

  const openAdd = () => {
    setDraft({
      ...emptyDraft(),
      category: categoryFilter !== 'all' ? categoryFilter : defaultCategory,
      ...(filterEmployee
        ? { employeeId: filterEmployee.employeeId, employeeLabel: filterEmployee.label }
        : {}),
    })
    setPhotoIndex(0)
    setPhotoError(null)
    setMessage(null)
    setModalOpen(true)
  }

  const openEdit = (row: AdvanceTransaction) => {
    if (row.scheduled) return
    setDraft(draftFromRow(row))
    setPhotoIndex(0)
    setPhotoError(null)
    setMessage(null)
    setModalOpen(true)
  }

  const openDuplicate = (row: AdvanceTransaction) => {
    if (row.scheduled) return
    setDraft({ ...draftFromRow(row), id: undefined })
    setPhotoIndex(0)
    setPhotoError(null)
    setMessage(null)
    setModalOpen(true)
  }

  const filterToEmployee = (row: EmployeeBalanceRow) => {
    setFilterEmployee({ employeeId: row.employeeId, label: row.employeeName || row.employeeId })
    setView('journal')
  }

  const appendPhotos = (added: string[]) => {
    if (!added.length) return
    const room = Math.max(0, MAX_ADVANCE_IMAGES - draft.imageUrls.length)
    if (added.length > room) {
      setPhotoError(`You can attach up to ${MAX_ADVANCE_IMAGES} photos.`)
    }
    setDraft((prev) => ({
      ...prev,
      imageUrls: [...prev.imageUrls, ...added.slice(0, Math.max(0, MAX_ADVANCE_IMAGES - prev.imageUrls.length))],
    }))
  }

  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return
    setPhotoBusy(true)
    setPhotoError(null)
    try {
      appendPhotos(await compressImageFiles(files))
    } catch (err: unknown) {
      const msg = err instanceof Error && err.message ? err.message : ''
      setPhotoError(
        /memory|allocate|quota/i.test(msg)
          ? 'Not enough memory to process that photo. Try a smaller image.'
          : msg || 'Could not process photo. Try a smaller image.',
      )
    } finally {
      setPhotoBusy(false)
    }
  }

  const removePhoto = (idx: number) => {
    setDraft((prev) => ({ ...prev, imageUrls: prev.imageUrls.filter((_, i) => i !== idx) }))
  }

  const employeeAmount = Number(draft.amount || 0) - Math.max(0, Number(draft.employer || 0))
  const runningAfterDraft = useMemo(() => {
    if (!draft.employeeId) return null
    const locked = draft.balance.trim() === '' ? undefined : Number(draft.balance)
    const fake: RunningBalanceRow = {
      id: '__draft',
      date: draft.date ? `${draft.date}T12:00:00.000Z` : null,
      employeeId: draft.employeeId,
      employeeAmount,
      establishedBalance: Number.isFinite(locked as number) ? locked : undefined,
    }
    const others: RunningBalanceRow[] = rows.filter((row) => (
      !row.scheduled
      && row.employeeId === draft.employeeId
      && row.id !== draft.id
    ))
    const found = withEmployeeRunningBalances([...others, fake]).find((row) => row.id === '__draft')
    return found?.runningBalance ?? null
  }, [rows, draft.employeeId, draft.date, draft.id, draft.balance, employeeAmount])
  const amountNum = Number(draft.amount || 0)
  const employerNum = Number(draft.employer || 0)
  const lockedNum = draft.balance.trim() === '' ? null : Number(draft.balance)
  const amountOk = Number.isFinite(amountNum)
    && Number.isFinite(employerNum)
    && (lockedNum == null || Number.isFinite(lockedNum))
    && ledgerDraftHasValue({
      amount: amountNum,
      employer: employerNum,
      employee: employeeAmount,
      balance: lockedNum,
    })

  const requestDeleteTransaction = async (row: {
    id: string
    employeeId: string
    employeeLabel?: string
    description: string
    amount: number | string
  }) => {
    if (!canDelete) return false
    const label = row.employeeLabel || row.employeeId
    const description = row.description.trim() || 'Untitled'
    return Boolean(await confirmPassword({
      title: 'Confirm password to delete transaction',
      description: `Re-enter your account password to permanently delete ${label}: ${description} (${formatUsd(Number(row.amount || 0))}).`,
      confirmLabel: 'Delete transaction',
      onConfirm: async (currentPassword) => {
        await eeapService.deleteTransaction(row.id, currentPassword)
        return true
      },
    }))
  }

  const requestDeleteFromDraft = async () => {
    if (!draft.id || !canDelete) return
    const deleted = await requestDeleteTransaction({
      id: draft.id,
      employeeId: draft.employeeId,
      employeeLabel: draft.employeeLabel,
      description: draft.description,
      amount: draft.amount,
    })
    if (!deleted) return
    setModalOpen(false)
    setMessage('Transaction deleted.')
    await load()
  }

  const requestDeleteFromJournal = async (row: AdvanceTransaction) => {
    if (row.scheduled || !row.id || !canDelete) return
    const deleted = await requestDeleteTransaction({
      id: row.id,
      employeeId: row.employeeId,
      employeeLabel: row.employeeName,
      description: row.description,
      amount: row.amount,
    })
    if (!deleted) return
    setMessage('Transaction deleted.')
    await load()
  }

  const saveDraft = async () => {
    if (!draft.employeeId) {
      setError('Select an employee')
      return
    }
    if (draft.balance.trim() !== '' && !Number.isFinite(Number(draft.balance))) {
      setError('Enter a valid balance or leave it blank')
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const payload: AdvanceTransactionInput = {
        employeeId: draft.employeeId,
        date: draft.date,
        category: draft.category,
        itemCategory: draft.itemCategory,
        amount: Number(draft.amount || 0),
        employerContribution: Number(draft.employer || 0),
        description: draft.description,
        establishedBalance: draft.balance.trim() === '' ? null : Number(draft.balance),
        imageUrls: draft.imageUrls,
      }
      if (draft.id) await eeapService.updateTransaction({ ...payload, id: draft.id })
      else await eeapService.addTransaction(payload)
      setModalOpen(false)
      setMessage(draft.id ? 'Transaction updated.' : 'Transaction recorded.')
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save transaction')
    } finally {
      setSaving(false)
    }
  }

  const journalSourceCount = rows.filter((row) => {
    if (!includeScheduled && row.scheduled) return false
    if (excludeDeductions && isDeductionRow(row)) return false
    if (deductionsOnly && !isDeductionRow(row)) return false
    return true
  }).length
  const resultCount = view === 'journal'
    ? `${journalRows.length} of ${journalSourceCount}`
    : `${balanceRows.length} employees`

  return (
    <div className="card bg-base-100 shadow-xl flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="card-body flex min-h-0 flex-1 flex-col overflow-hidden">
        <h2 className="card-title shrink-0">
          <FontAwesomeIcon icon={faReceipt} />
          Advances
        </h2>

        <div role="tablist" className="tabs tabs-boxed w-fit mt-2 shrink-0">
          {VIEWS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`tab ${view === item.id ? 'tab-active' : ''}`}
              onClick={() => setView(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="mb-3 flex flex-col gap-2 w-full shrink-0 mt-3">
          <div className="flex items-center gap-2 w-full">
            <label className="input input-bordered flex items-center gap-2 min-w-0 flex-1 min-h-11">
              <FontAwesomeIcon icon={faSearch} className="text-base-content/60 shrink-0" />
              <input
                type="text"
                className="grow min-w-0"
                placeholder="Search name, ID, category, item, description, amount…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && search.trim()) pinSearch()
                  if (e.key === 'Escape' && hasActiveFilters) clearAllFilters()
                }}
                aria-label="Search advances"
              />
              {search ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-circle shrink-0"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              ) : null}
            </label>
            <button type="button" className="btn btn-primary h-11 min-h-0 rounded-box px-3" onClick={openAdd}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          <div className="flex flex-wrap items-start gap-3">
            <div className="w-56">
              <Field label="Employee" compact>
                <EmployeeTypeahead
                  selected={filterEmployee}
                  onSelect={(emp) => setFilterEmployee({
                    employeeId: emp.employeeId,
                    label: suggestionLabel(emp),
                  })}
                  onClear={() => setFilterEmployee(null)}
                  placeholder="Filter by employee…"
                  app="profile"
                  capability="hr.searchEmployees"
                />
              </Field>
            </div>
            <div className="w-44">
              <Field label="From" compact>
                <DaisyDatePicker value={dateFrom} onChange={setDateFrom} aria-label="From date" />
              </Field>
            </div>
            <div className="w-44">
              <Field label="To" compact>
                <DaisyDatePicker value={dateTo} onChange={setDateTo} aria-label="To date" />
              </Field>
            </div>
            <div className="w-52">
              <Field label="Category" compact>
                <select
                  className="select select-bordered select-sm w-full"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
                >
                  <option value="all">All categories</option>
                  {categoryOptions.map((item) => (
                    <option key={item.key} value={item.key}>{item.label}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="w-52">
              <Field label="Item" compact>
                <select
                  className="select select-bordered select-sm w-full"
                  value={itemCategoryFilter}
                  onChange={(e) => setItemCategoryFilter(e.target.value)}
                >
                  <option value="all">All items</option>
                  <option value="none">No item</option>
                  {itemCategoryOptions.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </Field>
            </div>
            {view === 'journal' ? (
              <div className="w-56">
                <Field label="Show" compact>
                  <select
                    className="select select-bordered select-sm w-full"
                    value={journalDisplay}
                    onChange={(e) => setJournalDisplay(e.target.value as JournalDisplay)}
                    aria-label="Show"
                  >
                    {JOURNAL_DISPLAY_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            ) : null}
            <button
              type="button"
              className="btn btn-ghost btn-sm mt-5"
              disabled={!hasActiveFilters}
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 min-h-5">
            {filters.length > 0 ? (
              <>
                {filters.map((filter, idx) => (
                  <button
                    key={`${filter}-${idx}`}
                    type="button"
                    className="badge badge-info gap-2 cursor-pointer h-7"
                    onClick={() => setFilters((prev) => prev.filter((_, i) => i !== idx))}
                    title="Remove filter"
                  >
                    {filter}{' '}
                    <FontAwesomeIcon icon={faTimes} className="text-[10px]" aria-hidden />
                  </button>
                ))}
              </>
            ) : (
              <p className="text-xs text-base-content/50">
                Type to filter · Enter pins a term · Esc clears
              </p>
            )}
            <span className="text-xs text-base-content/60 ml-auto">
              {view === 'journal' && !showJournalBalance ? 'Balance shows when one employee is in view · ' : null}
              {resultCount}
            </span>
          </div>
        </div>

        {error && <div className="alert alert-error shrink-0"><span>{error}</span></div>}

        {loading ? (
          <div className="py-8 text-base-content/70 shrink-0">Loading transactions…</div>
        ) : view === 'journal' ? (
          <JournalView
            rows={journalRows}
            totals={journalTotals}
            dateSort={dateSort}
            sortField={sortField}
            onDateSortChange={setDateSort}
            onSortFieldChange={setSortField}
            showTotals={!includeScheduled}
            showBalance={showJournalBalance}
            balanceTitle={journalBalanceTitle(categoryFilter, ledgerCategories)}
            catalog={ledgerCategories}
            onEdit={openEdit}
            onDuplicate={openDuplicate}
            onDelete={(row) => { void requestDeleteFromJournal(row) }}
            canDelete={canDelete}
            onViewPhotos={(row) => openReceipt(openDocument, row.imageUrls)}
          />
        ) : (
          <BalancesView
            rows={balanceRows}
            totals={balanceTotals}
            categoryKeys={balanceCategoryKeys}
            catalog={ledgerCategories}
            showSplit={showCategorySplit}
            showAllowance={showCategorySplit}
            onOpen={filterToEmployee}
          />
        )}
      </div>

      {modalOpen && (
        <div className="modal modal-open z-[999]">
          <div className="modal-box max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col p-0 overflow-hidden shadow-2xl">
            <div className="shrink-0 px-6 py-4 border-b border-base-300">
              <h3 className="font-bold text-lg">{draft.id ? 'Edit transaction' : 'Record transaction'}</h3>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-3">
              <Field label="Employee">
                <EmployeeTypeahead
                  selected={draft.employeeId ? { employeeId: draft.employeeId, label: draft.employeeLabel } : null}
                  onSelect={(emp) => setDraft((prev) => ({
                    ...prev,
                    employeeId: emp.employeeId,
                    employeeLabel: suggestionLabel(emp),
                  }))}
                  onClear={() => setDraft((prev) => ({ ...prev, employeeId: '', employeeLabel: '' }))}
                  app="profile"
                  capability="hr.searchEmployees"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Category">
                  <select
                    className="select select-bordered w-full"
                    value={draft.category}
                    onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value as AdvanceCategory }))}
                  >
                    {draftCategoryOptions.map((item) => (
                      <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Item">
                  <select
                    className="select select-bordered w-full"
                    value={draft.itemCategory}
                    onChange={(e) => setDraft((prev) => ({ ...prev, itemCategory: e.target.value }))}
                  >
                    <option value="">No item</option>
                    {draftItemOptions.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Date">
                <DaisyDatePicker
                  className="w-full"
                  size="md"
                  value={draft.date}
                  onChange={(value) => setDraft((prev) => ({ ...prev, date: value }))}
                  trailing={(
                    <button
                      type="button"
                      className="btn btn-square join-item"
                      aria-label="Next payday"
                      title="Jump to the next payday, or the nearest payday if this date is not one"
                      disabled={saving}
                      onClick={() => setDraft((prev) => ({
                        ...prev,
                        date: stepToPayDate(
                          { ...DEFAULT_OPERATION_CONFIG, ...(payPeriodConfig || {}) },
                          prev.date,
                        ),
                      }))}
                    >
                      <FontAwesomeIcon icon={faAnglesRight} />
                    </button>
                  )}
                />
              </Field>
              <Field label="Transaction amount">
                <MoneyInput
                  value={draft.amount}
                  onChange={(amount) => setDraft((prev) => ({ ...prev, amount }))}
                  aria-label="Transaction amount"
                />
                <span className="text-xs text-base-content/60">Negative amount records a deduction.</span>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Employer contribution">
                  <MoneyInput
                    value={draft.employer}
                    onChange={(employer) => setDraft((prev) => ({ ...prev, employer }))}
                    allowEmpty={false}
                    aria-label="Employer contribution"
                  />
                  <span className="text-xs text-base-content/60">Negative contribution restores LTS allowance for the current anniversary year. It does not change the employee amount.</span>
                </Field>
                <Field label="Employee amount">
                  <input className="input input-bordered w-full" value={formatUsd(employeeAmount)} readOnly />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Running balance after this">
                  <input
                    className="input input-bordered w-full"
                    value={runningAfterDraft == null ? '—' : formatUsd(runningAfterDraft)}
                    readOnly
                  />
                  <span className="text-xs text-base-content/60">
                    Calculated from this employee's prior transactions. Later rows update automatically.
                  </span>
                </Field>
                <Field label="Lock balance (optional)">
                  <MoneyInput
                    value={draft.balance}
                    onChange={(balance) => setDraft((prev) => ({ ...prev, balance }))}
                    placeholder="Leave blank"
                    aria-label="Lock balance"
                  />
                  <span className="text-xs text-base-content/60">
                    Only set this to correct a known statement amount. Later running balances start from that lock.
                  </span>
                </Field>
              </div>
              <Field label="Description">
                <textarea
                  className="textarea textarea-bordered w-full min-h-24"
                  rows={3}
                  value={draft.description}
                  onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                />
              </Field>
              <section className="rounded-box border border-base-200 bg-base-200/30 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Photos</span>
                  {photoBusy ? <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary" /> : null}
                </div>
                <p className="text-xs text-base-content/60 mb-2">
                  Receipts or other photos, up to {MAX_ADVANCE_IMAGES}. Tap a photo to view it larger.
                </p>
                <PhotoUploadPreview
                  images={draft.imageUrls}
                  alt="Receipt"
                  disabled={saving || photoBusy}
                  galleryIndex={photoIndex}
                  onGalleryIndexChange={setPhotoIndex}
                  onUpload={(files) => { void addPhotos(files) }}
                  onAddDataUrls={appendPhotos}
                  onRemove={removePhoto}
                  onPreview={(idx) => openReceipt(openDocument, draft.imageUrls, idx)}
                />
                {photoError ? <p className="text-xs text-error mt-2">{photoError}</p> : null}
              </section>
            </div>
            <div className="shrink-0 flex items-center justify-between gap-2 px-6 py-4 border-t border-base-300 bg-base-100">
              {draft.id && canDelete ? (
                <button
                  type="button"
                  className="btn btn-ghost text-error"
                  disabled={saving}
                  onClick={() => { void requestDeleteFromDraft() }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Delete
                </button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving || photoBusy || !draft.employeeId || !amountOk || !draft.description.trim()}
                  onClick={() => void saveDraft()}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setModalOpen(false)}></div>
        </div>
      )}
      {message ? (
        <div className="toast toast-bottom toast-end z-[70] mb-4">
          <div role="alert" className="alert alert-success shadow-lg">
            <span>{message}</span>
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-circle"
              aria-label="Dismiss"
              onClick={() => setMessage(null)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      ) : null}
      {confirmPasswordModal}
    </div>
  )
}

function ScrollableLedger({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-0 min-h-0 flex-1 flex-col">
      <div className="h-fit max-h-full overflow-auto rounded-box border border-base-300">
        {children}
      </div>
    </div>
  )
}

function JournalView({
  rows,
  totals,
  dateSort,
  sortField,
  onDateSortChange,
  onSortFieldChange,
  showTotals,
  showBalance,
  balanceTitle,
  catalog,
  onEdit,
  onDuplicate,
  onDelete,
  canDelete,
  onViewPhotos,
}: {
  rows: AdvanceTransaction[]
  totals: ReturnType<typeof periodTotals>
  dateSort: DateSort
  sortField: LedgerSortField
  onDateSortChange: (next: DateSort) => void
  onSortFieldChange: (next: LedgerSortField) => void
  showTotals: boolean
  showBalance: boolean
  balanceTitle: string
  catalog: AdvanceCategoryDef[]
  onEdit: (row: AdvanceTransaction) => void
  onDuplicate: (row: AdvanceTransaction) => void
  onDelete: (row: AdvanceTransaction) => void
  canDelete: boolean
  onViewPhotos: (row: AdvanceTransaction) => void
}) {
  const colCount = showBalance ? 10 : 9
  const sortChip = (field: LedgerSortField, name: string, ascLabel: string, descLabel: string) => (
    <LedgerSortChip
      value={sortField === field ? dateSort : 'asc'}
      active={sortField === field}
      onActivate={() => {
        onSortFieldChange(field)
        onDateSortChange('asc')
      }}
      onChange={onDateSortChange}
      name={name}
      ascLabel={ascLabel}
      descLabel={descLabel}
    />
  )
  const head = (label: string, field: LedgerSortField, name: string, ascLabel: string, descLabel: string) => (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {sortChip(field, name, ascLabel, descLabel)}
    </span>
  )
  return (
    <ScrollableLedger>
      <table className="table table-sm table-pin-rows">
        <thead>
          <tr className="bg-base-200">
            <th>{head('Date', 'date', 'date', 'Earliest', 'Latest')}</th>
            <th>{head('Employee', 'employee', 'employee', 'A–Z', 'Z–A')}</th>
            <th>{head('Category', 'category', 'category', 'A–Z', 'Z–A')}</th>
            <th>{head('Item', 'itemCategory', 'item', 'A–Z', 'Z–A')}</th>
            <th>Description</th>
            <th>{head('Amount', 'amount', 'amount', 'Low to high', 'High to low')}</th>
            <th>{head('Employer', 'employer', 'employer', 'Low to high', 'High to low')}</th>
            <th>{head('Employee', 'employeeAmount', 'employee amount', 'Low to high', 'High to low')}</th>
            {showBalance ? (
              <th title={balanceTitle}>Balance</th>
            ) : null}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-base-content/60">No transactions match.</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.id}>
                <td>{formatLedgerDate(row.date)}</td>
                <td>
                  <EmployeeCell name={row.employeeName} employeeId={row.employeeId} />
                </td>
                <td>{row.categoryLabel || categoryLabel(row.category, catalog)}</td>
                <td>{row.itemCategory || '—'}</td>
                <td>
                  <AdvanceDescription
                    description={row.description}
                    scheduled={row.scheduled}
                    photoCount={receiptUrls(row).length}
                    onViewPhotos={() => onViewPhotos(row)}
                  />
                </td>
                <MoneyTd value={Number(row.amount || 0)} />
                <MoneyTd value={Number(row.employerContribution || 0)} positiveGreen />
                <MoneyTd value={Number(row.employeeAmount || 0)} />
                {showBalance ? (
                  <td className={`font-medium ${moneyValueClass(row.runningBalance) || ''}`}>
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      {formatUsd(row.runningBalance)}
                      {hasEstablishedBalance(row) ? <LockedBalanceBadge /> : null}
                    </span>
                  </td>
                ) : null}
                <td className="whitespace-nowrap">
                  {row.scheduled ? null : (
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        aria-label="Edit"
                        title="Edit"
                        onClick={() => onEdit(row)}
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-circle"
                        aria-label="Duplicate"
                        title="Duplicate"
                        onClick={() => onDuplicate(row)}
                      >
                        <FontAwesomeIcon icon={faClone} />
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-xs btn-circle text-error"
                          aria-label="Delete"
                          title="Delete"
                          onClick={() => onDelete(row)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
        {showTotals && rows.length > 0 ? (
          <tfoot>
            <tr className="bg-base-200">
              <th colSpan={5}>Period totals ({rows.length})</th>
              <th className={moneyValueClass(totals.amount)}>{formatUsd(totals.amount)}</th>
              <th className={moneyValueClass(totals.employer, { positiveGreen: true })}>{formatUsd(totals.employer)}</th>
              <th className={moneyValueClass(totals.employee)}>{formatUsd(totals.employee)}</th>
              {showBalance ? <th></th> : null}
              <th></th>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </ScrollableLedger>
  )
}

function BalancesView({
  rows,
  totals,
  categoryKeys,
  catalog,
  showSplit,
  showAllowance,
  onOpen,
}: {
  rows: EmployeeBalanceRow[]
  totals: { byCategory: Record<string, number>; total: number; remainingAllowance: number }
  categoryKeys: string[]
  catalog: AdvanceCategoryDef[]
  showSplit: boolean
  showAllowance: boolean
  onOpen: (row: EmployeeBalanceRow) => void
}) {
  const colCount = (showSplit ? categoryKeys.length : 0) + (showAllowance ? 1 : 0) + 3
  return (
    <ScrollableLedger>
      <table className="table table-sm table-pin-rows">
        <thead>
          <tr className="bg-base-200">
            <th>Employee</th>
            {showSplit ? categoryKeys.map((key) => (
              <th key={key}>{categoryLabel(key, catalog)}</th>
            )) : null}
            <th>Total owed</th>
            {showAllowance ? (
              <th title="Remaining of the $200 LTS allowance for the current hire-anniversary year">
                Allowance available
              </th>
            ) : null}
            <th>Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="text-base-content/60">No employee balances match.</td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr
                key={row.employeeId}
                className="cursor-pointer hover:bg-base-200/60"
                onClick={() => onOpen(row)}
                title="Show this employee in the journal"
              >
                <td>
                  <EmployeeCell name={row.employeeName} employeeId={row.employeeId} />
                </td>
                {showSplit ? categoryKeys.map((key) => (
                  <MoneyTd key={key} value={row.byCategory[key] || 0} />
                )) : null}
                <MoneyTd value={row.total} />
                {showAllowance ? <MoneyTd value={row.remainingAllowance} positiveGreen /> : null}
                <td>{formatLedgerDate(row.lastActivity)}</td>
              </tr>
            ))
          )}
        </tbody>
        {rows.length > 0 ? (
          <tfoot>
            <tr className="bg-base-200">
              <th>Totals ({rows.length})</th>
              {showSplit ? categoryKeys.map((key) => (
                <th key={key} className={moneyValueClass(totals.byCategory[key] || 0)}>{formatUsd(totals.byCategory[key] || 0)}</th>
              )) : null}
              <th className={moneyValueClass(totals.total)}>{formatUsd(totals.total)}</th>
              {showAllowance ? (
                <th className={moneyValueClass(totals.remainingAllowance, { positiveGreen: true })}>
                  {formatUsd(totals.remainingAllowance)}
                </th>
              ) : null}
              <th></th>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </ScrollableLedger>
  )
}

export default HrAdvancesTab
